import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, LockKey, FloppyDisk, Plus, PencilSimple, Trash, ClockCounterClockwise, ShieldCheck, Key,
  FilePdf, FileText, UploadSimple, DownloadSimple,
} from "@phosphor-icons/react";
import { SECTION_CONFIG, CORE_GROUPS } from "./config";
import RecordDialog, { FieldInput } from "./RecordDialog";

function maskDisplay(v) {
  return v === "••••••" ? <span className="tracking-widest text-muted-foreground">••••••</span> : (v || "—");
}

export default function File201Detail() {
  const { eid } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [core, setCore] = useState(null);
  const [tab, setTab] = useState("overview");

  const loadCore = useCallback(async () => {
    try {
      const { data } = await api.get(`/201/employees/${eid}`);
      setCore(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      navigate("/file201");
    }
  }, [eid, navigate]);

  useEffect(() => { loadCore(); }, [loadCore]);

  const exportPdf = async () => {
    try {
      const res = await api.get(`/201/employees/${eid}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `201_File_${(core?.full_name || "employee").replace(/\s/g, "_")}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("201 file exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  if (!core) return <div className="py-20 text-center text-muted-foreground">Loading...</div>;

  const initials = (core.full_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-6" data-testid="file201-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/file201")} data-testid="back-button" className="gap-2 -ml-2">
        <ArrowLeft className="h-4 w-4" /> Back to 201 files
      </Button>

      <Card className="border-border shadow-none overflow-hidden">
        <div className="bg-primary p-6 flex flex-wrap items-center gap-4 text-white">
          <div className="h-16 w-16 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-xl font-heading font-bold">
            {core.picture ? <img src={core.picture} alt="" className="h-16 w-16 rounded-full object-cover" /> : initials}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl font-bold">{core.full_name}</h1>
            <p className="text-white/80 text-sm">{core.job_title || "—"} · {core.department || "—"}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-white/15 text-white border-white/20 hover:bg-white/20">ID: {core.employee_code || "—"}</Badge>
              <Badge className="bg-white/15 text-white border-white/20 hover:bg-white/20 capitalize">{core.role}</Badge>
              {core.status && <Badge className="bg-white/15 text-white border-white/20 hover:bg-white/20">{core.status}</Badge>}
              {core.employment_status === "resigned" && (
                <Badge className="bg-destructive/90 text-white border-white/20 hover:bg-destructive" data-testid="file201-detail-resigned-badge">
                  Resigned{core.resign_date ? ` · ${core.resign_date}` : ""}
                </Badge>
              )}
            </div>
          </div>
          {isAdmin && (
            <Button onClick={exportPdf} data-testid="export-pdf-button"
              className="bg-white text-primary hover:bg-white/90 gap-2 font-semibold">
              <FilePdf weight="duotone" className="h-4 w-4" /> Export PDF
            </Button>
          )}
          {!isAdmin && (
            <div className="flex items-center gap-2 text-white/80 text-xs bg-white/10 rounded-lg px-3 py-2">
              <LockKey weight="duotone" className="h-4 w-4" /> Read-only · sensitive fields masked
            </div>
          )}
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto" data-testid="file201-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          {Object.keys(SECTION_CONFIG).map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`tab-${s}`}>{SECTION_CONFIG[s].label}</TabsTrigger>
          ))}
          <TabsTrigger value="leaves" data-testid="tab-leaves">Leave</TabsTrigger>
          {isAdmin && <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>}
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Trail</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <CoreEditor core={core} isAdmin={isAdmin} eid={eid} onSaved={loadCore} />
        </TabsContent>

        {Object.keys(SECTION_CONFIG).map((s) => (
          <TabsContent key={s} value={s} className="mt-6">
            <SectionTable eid={eid} section={s} isAdmin={isAdmin} active={tab === s} />
          </TabsContent>
        ))}

        <TabsContent value="leaves" className="mt-6"><LeaveView eid={eid} active={tab === "leaves"} /></TabsContent>
        {isAdmin && <TabsContent value="documents" className="mt-6"><DocumentsView eid={eid} active={tab === "documents"} /></TabsContent>}
        <TabsContent value="audit" className="mt-6"><AuditView eid={eid} active={tab === "audit"} /></TabsContent>
      </Tabs>
    </div>
  );
}

function CoreEditor({ core, isAdmin, eid, onSaved }) {
  const [form, setForm] = useState(core);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(core); }, [core]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      CORE_GROUPS.forEach((g) => g.fields.forEach((f) => { payload[f.key] = form[f.key] ?? ""; }));
      payload.employment_history = form.employment_history || [];
      payload.education = form.education || [];
      await api.put(`/201/employees/${eid}`, payload);
      toast.success("201 file updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const updateList = (key, idx, subkey, val) => {
    const list = [...(form[key] || [])];
    list[idx] = { ...list[idx], [subkey]: val };
    set(key, list);
  };
  const addRow = (key, template) => set(key, [...(form[key] || []), template]);
  const removeRow = (key, idx) => set(key, (form[key] || []).filter((_, i) => i !== idx));

  return (
    <div className="space-y-6">
      {CORE_GROUPS.map((group) => (
        <Card key={group.title} className="p-6 border-border shadow-none">
          <div className="flex items-center gap-2 mb-4">
            {group.lock && <LockKey weight="duotone" className="h-4 w-4 text-primary" />}
            <h3 className="font-heading font-bold">{group.title}</h3>
            {group.lock && <span className="uppercase text-[10px] tracking-[0.2em] text-primary">Encrypted</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {group.fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label>{f.label}</Label>
                {isAdmin ? (
                  <FieldInput field={f} value={form[f.key]} testid={`core-${f.key}`} onChange={(v) => set(f.key, v)} />
                ) : (
                  <div data-testid={`core-view-${f.key}`} className="h-10 flex items-center px-3 rounded-lg border border-border bg-muted/40 text-sm">
                    {maskDisplay(form[f.key])}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      ))}

      <ListSection title="Employment History" items={form.employment_history || []} isAdmin={isAdmin}
        cols={[["employer", "Employer"], ["position", "Position"], ["start", "Start"], ["end", "End"]]}
        onChange={(i, k, v) => updateList("employment_history", i, k, v)}
        onAdd={() => addRow("employment_history", { employer: "", position: "", start: "", end: "" })}
        onRemove={(i) => removeRow("employment_history", i)} testid="employment" />

      <ListSection title="Educational Background" items={form.education || []} isAdmin={isAdmin}
        cols={[["school", "School"], ["degree", "Degree"], ["year", "Year"], ["certification", "Certification"]]}
        onChange={(i, k, v) => updateList("education", i, k, v)}
        onAdd={() => addRow("education", { school: "", degree: "", year: "", certification: "" })}
        onRemove={(i) => removeRow("education", i)} testid="education" />

      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="save-core-button" className="gap-2 font-semibold">
            <FloppyDisk weight="bold" className="h-4 w-4" /> {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      )}
    </div>
  );
}

function ListSection({ title, items, cols, isAdmin, onChange, onAdd, onRemove, testid }) {
  return (
    <Card className="p-6 border-border shadow-none">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold">{title}</h3>
        {isAdmin && <Button variant="outline" size="sm" onClick={onAdd} data-testid={`add-${testid}`} className="gap-1.5"><Plus className="h-4 w-4" /> Add</Button>}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4 border border-dashed border-border rounded-lg">No records.</p>
      ) : (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end p-3 rounded-lg border border-border" data-testid={`${testid}-row-${i}`}>
              {cols.map(([k, label]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs">{label}</Label>
                  {isAdmin ? (
                    <Input value={item[k] || ""} onChange={(e) => onChange(i, k, e.target.value)} className="h-9" />
                  ) : (
                    <p className="text-sm py-1.5">{item[k] || "—"}</p>
                  )}
                </div>
              ))}
              {isAdmin && (
                <Button variant="ghost" size="icon" className="text-destructive h-9 w-9" onClick={() => onRemove(i)} data-testid={`remove-${testid}-${i}`}>
                  <Trash className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function SectionTable({ eid, section, isAdmin, active }) {
  const cfg = SECTION_CONFIG[section];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode, record}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/201/employees/${eid}/sections/${section}`);
      setRows(data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [eid, section]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const save = async (form) => {
    try {
      if (dialog.mode === "edit") {
        await api.put(`/201/employees/${eid}/sections/${section}/${dialog.record.id}`, form);
        toast.success("Record updated");
      } else {
        await api.post(`/201/employees/${eid}/sections/${section}`, form);
        toast.success("Record added");
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/201/employees/${eid}/sections/${section}/${id}`);
      toast.success("Record removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const colDef = cfg.columns.map((c) => cfg.fields.find((f) => f.key === c));
  const hasSensitive = cfg.fields.some((f) => f.sensitive);

  return (
    <Card className="border-border shadow-none overflow-hidden">
      <div className="p-4 flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-2">
          <h3 className="font-heading font-bold">{cfg.label} Records</h3>
          {hasSensitive && !isAdmin && (
            <span className="text-xs text-muted-foreground flex items-center gap-1"><LockKey className="h-3.5 w-3.5" /> amounts masked</span>
          )}
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setDialog({ mode: "create", record: {} })} data-testid={`add-${section}`} className="gap-1.5 font-semibold">
            <Plus weight="bold" className="h-4 w-4" /> Add {cfg.label}
          </Button>
        )}
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              {colDef.map((f) => <TableHead key={f.key}>{f.label}</TableHead>)}
              {isAdmin && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={colDef.length + 1} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={colDef.length + 1} className="text-center py-10 text-muted-foreground">No {cfg.label.toLowerCase()} records.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} data-testid={`${section}-row-${r.id}`}>
                {colDef.map((f) => (
                  <TableCell key={f.key} className={f.key === cfg.columns[0] ? "font-medium" : "text-muted-foreground"}>
                    {f.sensitive ? maskDisplay(r[f.key]) : (r[f.key] || "—")}
                  </TableCell>
                ))}
                {isAdmin && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-${section}-${r.id}`}
                        onClick={() => setDialog({ mode: "edit", record: r })}><PencilSimple className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`delete-${section}-${r.id}`}
                        onClick={() => remove(r.id)}><Trash className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {dialog && (
        <RecordDialog open onClose={() => setDialog(null)} onSave={save} fields={cfg.fields}
          initial={dialog.mode === "edit" ? dialog.record : {}}
          title={`${dialog.mode === "edit" ? "Edit" : "Add"} ${cfg.label}`} />
      )}
    </Card>
  );
}

function LeaveView({ eid, active }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (active) api.get(`/201/employees/${eid}/leaves`).then(({ data }) => setRows(data)).catch(() => {});
  }, [eid, active]);
  const badge = { pending: "bg-warning/10 text-warning border-warning/20", approved: "bg-success/10 text-success border-success/20", rejected: "bg-destructive/10 text-destructive border-destructive/20" };
  return (
    <Card className="border-border shadow-none overflow-hidden">
      <div className="p-4 border-b border-border"><h3 className="font-heading font-bold">Leave Records</h3></div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-muted/50 hover:bg-muted/50">
            <TableHead>Type</TableHead><TableHead>Dates</TableHead><TableHead>Days</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">No leave records.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.leave_type}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{r.start_date} → {r.end_date}</TableCell>
                <TableCell>{r.days}</TableCell>
                <TableCell><Badge variant="outline" className={`capitalize ${badge[r.status]}`}>{r.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function DocumentsView({ eid, active }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/employees/${eid}/documents`);
      setDocs(data);
    } catch (e) { /* ignore */ }
  }, [eid]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", "attachment");
    try {
      await api.post(`/employees/${eid}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error("Failed to open document");
    }
  };

  const remove = async (doc) => {
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Document removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  return (
    <Card className="p-6 border-border shadow-none" data-testid="documents-view">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <LockKey weight="duotone" className="h-5 w-5 text-primary" />
          <h3 className="font-heading font-bold">Document Vault</h3>
          <span className="uppercase text-[10px] tracking-[0.2em] text-primary">Confidential</span>
        </div>
        <label className="cursor-pointer">
          <input type="file" className="hidden" onChange={handleUpload} data-testid="vault-upload-input" disabled={uploading} />
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3.5 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors">
            <UploadSimple weight="bold" className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload document"}
          </span>
        </label>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Attach contracts, IDs, and signed forms. Files are stored securely and accessible to administrators only.</p>
      {docs.length === 0 ? (
        <div className="py-10 text-center border border-dashed border-border rounded-lg">
          <FileText weight="duotone" className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {docs.map((doc) => (
            <div key={doc.id} data-testid={`vault-doc-${doc.id}`}
              className="flex items-center gap-3 p-3 rounded-lg border border-border transition-transform duration-150 hover:-translate-y-0.5">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <FileText weight="duotone" className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{doc.original_filename}</p>
                <p className="text-xs text-muted-foreground">{doc.doc_type} · {fmtSize(doc.size || 0)}</p>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => download(doc)}
                data-testid={`vault-download-${doc.id}`}><DownloadSimple className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(doc)}
                data-testid={`vault-delete-${doc.id}`}><Trash className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AuditView({ eid, active }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (active) api.get(`/201/employees/${eid}/audit`).then(({ data }) => setRows(data)).catch(() => {});
  }, [eid, active]);
  const actionColor = { create: "text-success", update: "text-primary", delete: "text-destructive", password_reset: "text-warning" };
  return (
    <Card className="p-6 border-border shadow-none" data-testid="audit-view">
      <div className="flex items-center gap-2 mb-5">
        <ClockCounterClockwise weight="duotone" className="h-5 w-5 text-primary" />
        <h3 className="font-heading font-bold">Audit Trail</h3>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No changes recorded yet.</p>
      ) : (
        <div className="relative pl-6 space-y-5 before:absolute before:left-2 before:top-1 before:bottom-1 before:w-px before:bg-border">
          {rows.map((a) => (
            <div key={a.id} className="relative" data-testid={`audit-${a.id}`}>
              <div className="absolute -left-[18px] top-1 h-3 w-3 rounded-full bg-primary border-2 border-background" />
              <div className="flex flex-wrap items-center gap-2">
                <span className={`font-semibold capitalize ${actionColor[a.action] || "text-foreground"}`}>{a.action.replace("_", " ")}</span>
                <span className="text-sm text-muted-foreground">· {a.section}</span>
                {a.changed_fields?.length > 0 && (
                  <span className="text-xs text-muted-foreground">({a.changed_fields.join(", ")})</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                by <span className="font-medium text-foreground">{a.actor_name}</span> ({a.actor_role}) · {new Date(a.timestamp).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
